import type { FileTemplate } from "schemas/generated/fileTemplate";

import * as React from "react";

import useShopDomain from "@/features/react-editor/hooks/useShopDomain";
import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import twMerge from "@replo/design-system/utils/twMerge";
import { useInterval } from "replo-utils/react/use-interval";

import { DevicePreview } from "./DevicePreview";

const TEMPLATE_CARD_CONSTANTS = {
  PREVIEW_HEIGHT: 400,
  MOBILE_MAX_WIDTH: 175,
} as const;

const AUTO_SCROLL_CONFIG = {
  DELAY_MS: 500,
  SPEED_PX_PER_FRAME: 6,
  INTERVAL_MS: 10,
} as const;

export function PageTemplateCardSkeleton() {
  return (
    <div className="flex flex-col h-full border-0.5 gap-4 border-border rounded-xl bg-white p-4">
      <div className="relative rounded-xl bg-white shadow-modal">
        <div style={{ height: `${TEMPLATE_CARD_CONSTANTS.PREVIEW_HEIGHT}px` }}>
          <Skeleton className="w-full h-full rounded-xl" />
        </div>
        <div className="absolute inset-0 rounded-xl pointer-events-none transition-colors duration-150 bg-white/30" />
      </div>

      <Skeleton className="h-5 w-2/3 rounded mb-1" />
    </div>
  );
}

export default function PageTemplateCard({
  template,
  onClick,
}: {
  template: FileTemplate;
  onClick?: () => void;
}) {
  const shopDomain = useShopDomain();
  const rendererContainerRef = React.useRef<HTMLDivElement | null>(null);
  const scrollPositionRef = React.useRef(0);
  const scrollTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const [isScrolling, setIsScrolling] = React.useState(false);

  useInterval(
    () => {
      const iframe = rendererContainerRef.current?.querySelector("iframe");
      if (!iframe?.contentWindow) {
        return;
      }

      const doc = iframe.contentWindow.document;
      const maxScroll =
        doc.documentElement.scrollHeight - doc.documentElement.clientHeight;

      if (scrollPositionRef.current >= maxScroll) {
        setIsScrolling(false);
        return;
      }

      scrollPositionRef.current += AUTO_SCROLL_CONFIG.SPEED_PX_PER_FRAME;
      iframe.contentWindow.scrollTo(0, scrollPositionRef.current);
    },
    isScrolling ? AUTO_SCROLL_CONFIG.INTERVAL_MS : null,
  );

  const startScrolling = () => {
    scrollTimeoutRef.current = setTimeout(
      () => setIsScrolling(true),
      AUTO_SCROLL_CONFIG.DELAY_MS,
    );
  };

  const stopScrolling = () => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = null;
    }
    setIsScrolling(false);
    scrollPositionRef.current = 0;
    const iframe = rendererContainerRef.current?.querySelector("iframe");
    iframe?.contentWindow?.scrollTo(0, 0);
  };

  return (
    <div
      className={twMerge(
        "group relative flex flex-col h-full w-full border-0.5 gap-4 rounded-xl border-border bg-white p-4 transition-all duration-200",
        onClick && "cursor-pointer hover:scale-[0.99] hover:shadow-lg",
      )}
      onClick={onClick}
      onMouseEnter={startScrolling}
      onMouseLeave={stopScrolling}
    >
      <div
        className="flex justify-center items-start overflow-hidden rounded-sm"
        style={{ height: `${TEMPLATE_CARD_CONSTANTS.PREVIEW_HEIGHT}px` }}
      >
        <DevicePreview
          pageRoute={`/template-preview/${template.id}`}
          className="w-full h-full pointer-events-none"
          isScrollable={false}
          hideScrollbars={true}
          domain={shopDomain}
          mobileClassName="h-full w-full"
          maxMobileWidth={TEMPLATE_CARD_CONSTANTS.MOBILE_MAX_WIDTH}
          desktopClassName="pointer-events-none rounded-[6px] h-full w-full border-[1px] border-border/25 hover:shadow-modal"
          rendererContainerRef={rendererContainerRef}
        />
      </div>

      <h4 className="header-h4 line-clamp-1">{template.name}</h4>
    </div>
  );
}
